import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { MarkupCatalog } from '@bloom/shared';
import apiClient from '../../lib/api';

// Base commission a sales rep earns on the list price, as a percent. Hardcoded
// for now. Keep in sync with the backend MarkupCatalog model constant.
const SALES_REP_BASE_COMMISSION_PCT = 35;

interface SalesRepCatalogModalProps {
  catalogs: MarkupCatalog[];
  onClose: () => void;
  onSaved?: () => void;
}

interface ProductRow {
  productId: string;
  name: string;
  cost: number;    // product base price (admin's cost)
  floor: number;   // catalog price = admin take (cost + admin markup)
}

const round2 = (n: number) => Math.round(n * 100) / 100;
// Must match the backend MarkupCatalog hook exactly: base commission = pct% of
// list, rounded to the nearest dollar; list = floor + commission. So the admin
// take (floor) + rep commission always equals the list price with no drift.
const baseCommissionFor = (floor: number) =>
  Math.round(floor * SALES_REP_BASE_COMMISSION_PCT / (100 - SALES_REP_BASE_COMMISSION_PCT));
const listFromFloor = (floor: number) => round2(floor + baseCommissionFor(floor));

/**
 * Admin-only panel: designate THE sales-rep catalog and set one global discount
 * ladder (percentages of rep commission). Sale prices are derived automatically
 * from each product's floor at a fixed SALES_REP_BASE_COMMISSION_PCT% base
 * commission; the panel just shows the resulting rep commission and admin take.
 */
export const SalesRepCatalogModal: React.FC<SalesRepCatalogModalProps> = ({ catalogs, onClose, onSaved }) => {
  const adminCatalogs = catalogs.filter(c => c.ownerRole === 'admin');
  const currentRepCatalog = adminCatalogs.find(c => (c as any).isSalesRepCatalog);

  const [selectedCatalogId, setSelectedCatalogId] = useState<string>(currentRepCatalog?._id || adminCatalogs[0]?._id || '');
  const [isRepCatalog, setIsRepCatalog] = useState<boolean>(!!currentRepCatalog);
  const [tierPercentsInput, setTierPercentsInput] = useState<string>('0, 25, 50, 75, 99');
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProducts = async (catalogId: string) => {
    if (!catalogId) { setRows([]); return; }
    setLoadingProducts(true);
    setError(null);
    const res = await apiClient.getCatalogProducts(catalogId);
    setLoadingProducts(false);
    if (!res.success || !res.data) {
      setError(res.error || 'Failed to load catalog products');
      setRows([]);
      return;
    }
    const data: any = res.data;
    setIsRepCatalog(!!data.isSalesRepCatalog);
    if (Array.isArray(data.salesRepTierPercents) && data.salesRepTierPercents.length > 0) {
      setTierPercentsInput(data.salesRepTierPercents.join(', '));
    }
    setRows(data.products.filter(Boolean).map((p: any) => ({
      productId: p._id,
      name: p.displayName || p.drugName || p._id,
      cost: p.basePrice ?? 0,
      floor: p.price ?? 0
    })));
  };

  React.useEffect(() => {
    loadProducts(selectedCatalogId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatalogId]);

  const parsePercents = (s: string): number[] =>
    Array.from(new Set(s.split(',').map(t => parseFloat(t.trim())).filter(t => !isNaN(t)))).sort((a, b) => a - b);

  const tierPercents = parsePercents(tierPercentsInput);
  const tierPercentsError = tierPercents.some(p => p < 0 || p > 99)
    ? 'Discount tiers must be percentages between 0 and 99 (a rep always keeps at least $1)'
    : null;

  const handleSave = async () => {
    setError(null);
    if (tierPercentsError) { setError(tierPercentsError); return; }
    setSaving(true);
    const res = await apiClient.updateSalesRepCatalogConfig(selectedCatalogId, {
      isSalesRepCatalog: isRepCatalog,
      tierPercents
    });
    setSaving(false);
    if (res.success) {
      onSaved?.();
      onClose();
    } else {
      setError(res.error || 'Failed to save sales-rep configuration');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sales-rep catalog</h3>
            <p className="text-xs text-gray-500">
              Reps earn a base commission of <strong>{SALES_REP_BASE_COMMISSION_PCT}%</strong> of the sale price.
              Sale prices are derived automatically from each product's cost.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {adminCatalogs.length === 0 ? (
            <div className="text-sm text-gray-600">No admin catalogs yet. Create a catalog with floor prices first, then designate it here.</div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Catalog</label>
                <select
                  value={selectedCatalogId}
                  onChange={(e) => setSelectedCatalogId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {adminCatalogs.map(c => (
                    <option key={c._id} value={c._id}>
                      {c.catalogName}{(c as any).isSalesRepCatalog ? ' — current sales-rep catalog' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={isRepCatalog}
                  onChange={(e) => setIsRepCatalog(e.target.checked)}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                />
                Designate this as <strong>the</strong> sales-rep catalog (reps are auto-assigned to it)
              </label>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Discount tiers — % of commission a rep may give up (comma-separated)
                </label>
                <input
                  type="text"
                  value={tierPercentsInput}
                  onChange={(e) => setTierPercentsInput(e.target.value)}
                  placeholder="0, 25, 50, 75, 100"
                  className={`w-full border rounded-md px-3 py-2 text-sm ${tierPercentsError ? 'border-red-300' : 'border-gray-300'}`}
                />
                {tierPercentsError && <p className="mt-1 text-xs text-red-600">{tierPercentsError}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Applies to every product. Reps see these as dollar amounts based on each product's commission.
                </p>
              </div>

              {loadingProducts ? (
                <div className="text-sm text-gray-500">Loading products…</div>
              ) : rows.length === 0 ? (
                <div className="text-sm text-gray-600">This catalog has no products. Add products (with floor prices) first.</div>
              ) : (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <div className="hidden md:grid grid-cols-12 gap-2 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
                    <div className="col-span-4">Product</div>
                    <div className="col-span-3">Admin take (cost + markup)</div>
                    <div className="col-span-2 text-right">Sale price</div>
                    <div className="col-span-3 text-right">Rep commission ({SALES_REP_BASE_COMMISSION_PCT}%)</div>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {rows.map(r => {
                      const list = listFromFloor(r.floor);
                      const commission = round2(list - r.floor);
                      const markup = round2(r.floor - r.cost);
                      return (
                        <div key={r.productId} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-12 md:col-span-4 text-sm text-gray-900">{r.name}</div>
                          <div className="col-span-6 md:col-span-3 text-sm text-gray-700">
                            ${r.floor.toFixed(2)}
                            <span className="block text-xs text-gray-400">${r.cost.toFixed(2)} cost + ${markup.toFixed(2)} markup</span>
                          </div>
                          <div className="col-span-3 md:col-span-2 text-right text-sm text-gray-900">${list.toFixed(2)}</div>
                          <div className="col-span-3 md:col-span-3 text-right text-sm font-medium text-green-600">${commission.toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !!tierPercentsError || adminCatalogs.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
